// Nothing inside /admin renders until the company is known.
//
// The problem this solves
// -----------------------
// AdminLayout builds the whole sidebar from sessionStorage, read during
// render:
//
//     const company = JSON.parse(sessionStorage.getItem('companyDetails') || 'null');
//     const isRestaurantOnly = (company?.propertType || …) === 'R';
//
// and about ninety other screens read the same three keys the moment they
// mount. sessionStorage is not reactive: writing to it does not re-render
// anything. So on the first screen after a login those keys were still empty,
// every one of those reads came back null, and a restaurant-only property was
// handed the hotel menu.
//
// The old cure was a full page reload from inside the Dashboard - set a flag,
// call window.location.reload(), and let the second boot find the keys in
// place. It worked, and it cost every user one entire extra start of the
// application: bundle re-parsed, every component re-mounted, every opening
// call made twice.
//
// The cure here is to ask first and render afterwards. One call to
// /account/init, the three keys written, and only then are the children
// mounted - so nothing downstream can ever read an empty session, and no
// reload is needed to put it right.
//
// Deliberately NOT a context: the ninety screens read sessionStorage directly
// and are left exactly as they are. This only guarantees that what they read
// is already there.
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import config from '../config';
import RouteSkeleton from './components/RouteSkeleton';

// Everything AdminLayout and the screens below it expect to find.
const writeSession = (d) => {
  sessionStorage.setItem('companyId', d.id);
  sessionStorage.setItem('companyDetails', JSON.stringify(d));
  sessionStorage.setItem('propertType', d?.propertType || '');
};

export default function CompanyGate({ children }) {
  // Already loaded in this tab - a navigation between admin screens must not
  // wait for the network again.
  const [ready, setReady] = useState(
    () => !!sessionStorage.getItem('companyDetails'),
  );
  const [failed, setFailed] = useState('');

  useEffect(() => {
    if (ready) return undefined;

    let alive = true;
    const load = async () => {
      const client = localStorage.getItem('client');
      if (!client) {
        // No client in storage means no login to speak of. Let the children
        // mount: ProtectedRoute above decides where such a user belongs.
        if (alive) setReady(true);
        return;
      }
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/account/init?client=${encodeURIComponent(client)}`,
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const d = await res.json();
        if (!alive) return;
        writeSession(d);
        setReady(true);
      } catch (e) {
        if (alive) setFailed(e.message || 'Could not load the company');
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [ready]);

  if (failed) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          Could not load your company
        </Typography>
        <Typography sx={{ color: '#6b7280', mb: 2 }}>{failed}</Typography>
        <Button
          variant='contained'
          onClick={() => {
            setFailed('');
            setReady(false);
          }}
          sx={{
            bgcolor: '#1d4ed8',
            backgroundImage: 'none',
            color: '#fff',
            '&:hover': { bgcolor: '#1e40af' },
          }}>
          Try again
        </Button>
      </Box>
    );
  }

  // The same outline the lazy routes show, so the wait looks like part of the
  // app opening rather than a stall.
  if (!ready) return <RouteSkeleton />;

  return children;
}
